import { c as _c } from "react/compiler-runtime";
import type { Address } from 'viem';
import { InterfaceChainUtils, type InterfaceInputToken, type Point, type Rewards } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { NetworkAvatar as Network } from '@morpho-org/ui-v6/client';
import { Button, Separator } from '@repo/ui';
import { useMemo } from 'react';
import type { Warning } from '@/components/common/WarningIcon';
import type { VaultV2ApyBreakdown } from '@/lib/procedures/vaultV2Apy.contract';
import { MobileCardRow } from '@/components/common/MobileCardRow';
import type { BasicCuratorInfo } from './MobileEarnCards.types';
import { type CollateralExposure, CollateralExposureStack } from '../CollateralExposureStack';
import MobileAmountRow from './components/MobileAmountRow/MobileAmountRow';
import MobileCuratorList from './components/MobileCuratorList/MobileCuratorList';
import MobileVaultCardHeader from './components/MobileVaultCardHeader/MobileVaultCardHeader';
import { useMobileEarnCard } from './hooks/useMobileEarnCard';
interface MobileVaultPositionCardProps {
  href: string;
  displayNetwork?: boolean;
  vault: {
    address: Address;
    name: string;
    asset: InterfaceInputToken;
    totalAssetsUsd: bigint | null | undefined;
    isVaultV2: boolean;
    warnings: Warning[];
  };
  apy: {
    native: bigint | null | undefined;
    net: bigint | null | undefined;
    rewards: Rewards[];
    performanceFee: bigint | null | undefined;
    managementFee?: bigint | null | undefined;
    maxRate?: bigint | null | undefined;
    maxApy?: number | null;
    isRateCapped?: boolean;
    includesDonations?: boolean;
    apyBreakdown?: VaultV2ApyBreakdown | null;
    points: Point[];
  };
  position: {
    amount: bigint | null;
    usdValue: bigint | null | undefined;
  };
  liquidity?: {
    liquidity: bigint | null | undefined;
    liquidityUsd: bigint | null | undefined;
  };
  curators: BasicCuratorInfo[];
  collateralExposures: CollateralExposure[];
  /**
   * Optional primary call-to-action rendered below the action buttons (e.g.
   * the V1→V2 upgrade button). When present, the Deposit button is rendered
   * as `secondary` so the CTA visually leads.
   */
  primaryCta?: React.ReactNode;
  /** False when browsing another address's portfolio — hides Deposit / Withdraw. */
  isSelf?: boolean;
}
const srcAvatarProps = {
  size: 'small'
} as const;
export default function MobileVaultPositionCard(t0) {
  const $ = _c(67);
  const {
    href,
    displayNetwork,
    vault,
    apy,
    position,
    liquidity,
    curators,
    collateralExposures,
    primaryCta,
    isSelf: t1
  } = t0;
  const isSelf = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== liquidity) {
    t2 = liquidity ? {
      amount: liquidity.liquidity,
      usdValue: liquidity.liquidityUsd
    } : undefined;
    $[0] = liquidity;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== isSelf || $[3] !== position.amount || $[4] !== position.usdValue || $[5] !== t2 || $[6] !== vault.address || $[7] !== vault.asset || $[8] !== vault.isVaultV2) {
    t3 = {
      vaultAddress: vault.address,
      asset: vault.asset,
      amount: position.amount,
      usdValue: position.usdValue,
      isSelf,
      liquidity: t2,
      isVaultV2: vault.isVaultV2
    };
    $[2] = isSelf;
    $[3] = position.amount;
    $[4] = position.usdValue;
    $[5] = t2;
    $[6] = vault.address;
    $[7] = vault.asset;
    $[8] = vault.isVaultV2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const {
    formattedUsdValue,
    formattedAmount,
    formattedLiquidityUsdValue,
    formattedLiquidityAmount,
    onDepositClick,
    onWithdrawClick,
    canDeposit,
    canWithdraw
  } = useMobileEarnCard(t3);
  let t4;
  if ($[10] !== vault.asset.chain.id) {
    t4 = InterfaceChainUtils.getMetadata(vault.asset.chain.id);
    $[10] = vault.asset.chain.id;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const networkName = t4.chain.name;
  const hasActions = canDeposit || canWithdraw || Boolean(primaryCta);
  let t5;
  if ($[12] !== vault.address || $[13] !== vault.asset.chain.id) {
    t5 = {
      kind: "vault",
      chainId: vault.asset.chain.id,
      identifier: vault.address
    };
    $[12] = vault.address;
    $[13] = vault.asset.chain.id;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== apy.apyBreakdown || $[16] !== apy.includesDonations || $[17] !== apy.isRateCapped || $[18] !== apy.managementFee || $[19] !== apy.maxApy || $[20] !== apy.maxRate || $[21] !== apy.native || $[22] !== apy.net || $[23] !== apy.performanceFee || $[24] !== apy.points || $[25] !== apy.rewards || $[26] !== href || $[27] !== t5 || $[28] !== vault.asset || $[29] !== vault.isVaultV2 || $[30] !== vault.name || $[31] !== vault.warnings) {
    t6 = <MobileVaultCardHeader href={href} asset={vault.asset} vaultName={vault.name} rewards={apy.rewards} points={apy.points} netApy={apy.net} apy={apy.native} performanceFee={apy.performanceFee} managementFee={apy.managementFee} maxRate={apy.maxRate} maxApy={apy.maxApy} isRateCapped={apy.isRateCapped} includesDonations={apy.includesDonations} isVaultV2={vault.isVaultV2} warnings={vault.warnings} merklContext={t5} apyBreakdown={apy.apyBreakdown} />;
    $[15] = apy.apyBreakdown;
    $[16] = apy.includesDonations;
    $[17] = apy.isRateCapped;
    $[18] = apy.managementFee;
    $[19] = apy.maxApy;
    $[20] = apy.maxRate;
    $[21] = apy.native;
    $[22] = apy.net;
    $[23] = apy.performanceFee;
    $[24] = apy.points;
    $[25] = apy.rewards;
    $[26] = href;
    $[27] = t5;
    $[28] = vault.asset;
    $[29] = vault.isVaultV2;
    $[30] = vault.name;
    $[31] = vault.warnings;
    $[32] = t6;
  } else {
    t6 = $[32];
  }
  let t7;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Separator className="my-xs" />;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  let t8;
  if ($[34] !== displayNetwork || $[35] !== networkName || $[36] !== vault.asset.chain.id) {
    t8 = displayNetwork && <MobileCardRow label="Network"><div className="gap-2xs flex items-center"><Network chainId={vault.asset.chain.id} srcAvatarProps={srcAvatarProps} /><span className="text-3xs capitalize">{networkName}</span></div></MobileCardRow>;
    $[34] = displayNetwork;
    $[35] = networkName;
    $[36] = vault.asset.chain.id;
    $[37] = t8;
  } else {
    t8 = $[37];
  }
  let t9;
  if ($[38] !== formattedAmount || $[39] !== formattedUsdValue || $[40] !== vault.asset.logoURI) {
    t9 = <MobileAmountRow label="Your Position" logoURI={vault.asset.logoURI} amount={formattedAmount} usdValue={formattedUsdValue} />;
    $[38] = formattedAmount;
    $[39] = formattedUsdValue;
    $[40] = vault.asset.logoURI;
    $[41] = t9;
  } else {
    t9 = $[41];
  }
  let t10;
  if ($[42] !== formattedLiquidityAmount || $[43] !== formattedLiquidityUsdValue || $[44] !== liquidity || $[45] !== vault.asset.logoURI) {
    t10 = isDefined(liquidity) && <MobileAmountRow label="Liquidity" logoURI={vault.asset.logoURI} amount={formattedLiquidityAmount} usdValue={formattedLiquidityUsdValue} />;
    $[42] = formattedLiquidityAmount;
    $[43] = formattedLiquidityUsdValue;
    $[44] = liquidity;
    $[45] = vault.asset.logoURI;
    $[46] = t10;
  } else {
    t10 = $[46];
  }
  let t11;
  if ($[47] !== curators) {
    t11 = curators.length > 0 && <MobileCardRow label="Curator"><MobileCuratorList curators={curators} /></MobileCardRow>;
    $[47] = curators;
    $[48] = t11;
  } else {
    t11 = $[48];
  }
  let t12;
  if ($[49] !== collateralExposures || $[50] !== vault.totalAssetsUsd) {
    t12 = <MobileCardRow label="Exposure"><CollateralExposureStack maxTokensCount={5} collateralExposures={collateralExposures} isMobileSupportEnabled={true} size="small" align="right" totalAssetsUsd={vault.totalAssetsUsd} /></MobileCardRow>;
    $[49] = collateralExposures;
    $[50] = vault.totalAssetsUsd;
    $[51] = t12;
  } else {
    t12 = $[51];
  }
  let t13;
  if ($[52] !== canDeposit || $[53] !== canWithdraw || $[54] !== hasActions || $[55] !== onDepositClick || $[56] !== onWithdrawClick || $[57] !== primaryCta) {
    t13 = hasActions && <><Separator className="my-xs" /><div className="gap-sm flex">{canDeposit && <Button className="flex-1 basis-1/2" onClick={onDepositClick} variant={primaryCta ? "secondary" : undefined}>Deposit</Button>}{canWithdraw && <Button className="flex-1 basis-1/2" variant="secondary" onClick={onWithdrawClick}>Withdraw</Button>}</div>{primaryCta}</>;
    $[52] = canDeposit;
    $[53] = canWithdraw;
    $[54] = hasActions;
    $[55] = onDepositClick;
    $[56] = onWithdrawClick;
    $[57] = primaryCta;
    $[58] = t13;
  } else {
    t13 = $[58];
  }
  let t14;
  if ($[59] !== t10 || $[60] !== t11 || $[61] !== t12 || $[62] !== t13 || $[63] !== t6 || $[64] !== t8 || $[65] !== t9) {
    t14 = <article className="text-foreground p-md gap-sm bg-card flex flex-col rounded-[20px] shadow-md dark:shadow-none" role="article">{t6}{t7}{t8}{t9}{t10}{t11}{t12}{t13}</article>;
    $[59] = t10;
    $[60] = t11;
    $[61] = t12;
    $[62] = t13;
    $[63] = t6;
    $[64] = t8;
    $[65] = t9;
    $[66] = t14;
  } else {
    t14 = $[66];
  }
  return t14;
}